import { useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { useTransition } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { gql } from '@/lib/graphql/gql'

const DELETE_WEBHOOK = gql`
  mutation ToggleWebhook($id: String!) {
    toggleWebhook(id: $id) {
      id
    }
  }
`

type Props = {
  isEnabled: boolean
  isOpen: boolean
  onClose: () => void
}

export const ToggleWebhookDialog = ({ isEnabled, isOpen, onClose }: Props) => {
  const { idWebhook = '' } = useParams()
  const [isPending, startTransition] = useTransition()

  const [deleteWebhook] = useMutation(DELETE_WEBHOOK)

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent onEscapeKeyDown={() => onClose()}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isEnabled ? 'Disable' : 'Enable'} webhook
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to{' '}
            <strong>{isEnabled ? 'disable' : 'enable'} the webhook</strong>?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() => {
              startTransition(async () => {
                try {
                  await deleteWebhook({ variables: { id: idWebhook } })
                  toast(
                    <ToasterContent variant="success">
                      Webhook is {isEnabled ? 'disabled' : 'enabled'}{' '}
                      successfully
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                  onClose()
                } catch {
                  toast(
                    <ToasterContent variant="error">
                      Failed to {isEnabled ? 'disable' : 'enable'} the webhook
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                }
              })
            }}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
